import React, { useState, useEffect, useRef } from 'react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { X, ChevronDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ApiService } from '../services/api';
import { getSourceIcon, getPlatformIcon } from './ui/brand-icons';
import { readUrlState, hasUrlState } from '../utils/urlState';

// Максимум одновременно выбранных кампаний — ограничение и для UI, и для
// восстановления состояния из URL/localStorage (см. DashboardContext.js)
export const MAX_SELECTED_CAMPAIGNS = 20;

// Обрезает список кампаний до MAX_SELECTED_CAMPAIGNS, не мутируя исходный массив.
// Единая точка усечения — используется и здесь, и в DashboardContext.js.
export const capCampaigns = (campaigns) => (campaigns || []).slice(0, MAX_SELECTED_CAMPAIGNS);

// Состояние из ссылки (читаем один раз, до первого рендера): если экран открыт
// по шаренной ссылке, источник и кампании берём из неё, а не из своего localStorage
const urlState = readUrlState();
const urlIsSource = hasUrlState();

const CampaignsFilter = ({ onFiltersChange, selectedPlatform, selectedProject, tempPlatform, onPlatformChange }) => {
  const { user, loading: authLoading } = useAuth();
  const [filterOptions, setFilterOptions] = useState({ campaigns: [], mediaSources: [] });
  const [loading, setLoading] = useState(false);
  
  // Выбранный источник
  const [selectedSource, setSelectedSource] = useState(() => {
    if (urlIsSource) return urlState.mediaSource || 'all';
    const saved = localStorage.getItem('selectedMediaSource');
    return saved || 'all';
  });
  
  // Читаем сохранённый выбор кампаний один раз (лениво, до первого рендера) —
  // нужен и список, и флаг "был ли урезан", чтобы показать подсказку
  const restoredCampaignsRef = useRef(null);
  if (restoredCampaignsRef.current === null) {
    let restored;
    if (urlIsSource) {
      restored = urlState.campaigns || [];
    } else {
      const saved = localStorage.getItem('selectedCampaigns');
      restored = [];
      if (saved) {
        try {
          restored = JSON.parse(saved);
        } catch (e) {
          console.error('Error parsing saved campaigns:', e);
        }
      }
    }
    restoredCampaignsRef.current = {
      campaigns: capCampaigns(restored),
      wasCapped: restored.length > MAX_SELECTED_CAMPAIGNS
    };
  }

  // Выбранные кампании
  const [selectedCampaigns, setSelectedCampaigns] = useState(
    () => restoredCampaignsRef.current.campaigns
  );

  // Подсказка под кнопками "Выбрать все" / "Снять все"
  const [selectAllHint, setSelectAllHint] = useState(() =>
    restoredCampaignsRef.current.wasCapped
      ? `Сохранённый выбор урезан до ${MAX_SELECTED_CAMPAIGNS} — максимум ${MAX_SELECTED_CAMPAIGNS} кампаний`
      : ''
  );

  // Открыт ли попап со списком кампаний
  const [isListOpen, setIsListOpen] = useState(false);
  
  // Поиск по кампаниям
  const [searchText, setSearchText] = useState(() => {
    return localStorage.getItem('campaignsSearchText') || '';
  });

  // Фильтр "только хорошие кампании".
  // По ссылке выключен: иначе он подменил бы шаренный набор кампаний своим
  const [showOnlyGoodCampaigns, setShowOnlyGoodCampaigns] = useState(() => {
    if (urlIsSource) return false;
    const saved = localStorage.getItem('showOnlyGoodCampaigns');
    return saved === 'true';
  });

  // Загрузка опций фильтров
  useEffect(() => {
    // Не загружаем данные пока не авторизован пользователь
    if (authLoading || !user) {
      return;
    }

    const loadFilterOptions = async () => {
      setLoading(true);
      try {
        const result = await ApiService.get('/filters/options');
        setFilterOptions(result);
      } catch (error) {
        console.error('Error loading filter options:', error);
      } finally {
        setLoading(false);
      }
    };

    loadFilterOptions();
  }, [authLoading, user]);

  // Функция проверки "хорошей" кампании
  const isGoodCampaign = (campaignName) => {
    const upperName = campaignName.toUpperCase();
    const isPotentiallyGood = upperName.includes('_PU_') || upperName.includes('ROAS');
    const isBad = upperName.includes('CRETEST');
    return isPotentiallyGood && !isBad;
  };

  // Функция для сокращения названий источников
  const getShortSourceName = (source) => {
    const sourceMap = {
      'Facebook Ads': 'FB',
      'applovin_int': 'APP',
      'googleadwords_int': 'GOOGLE'
    };
    
    return sourceMap[source] || source;
  };

  // Фильтрация кампаний по всем критериям
  const filteredCampaigns = filterOptions.campaigns.filter(camp => {
    // Фильтр по платформе
    const matchesPlatform = !selectedPlatform || selectedPlatform === 'all' || camp.pf === selectedPlatform;
    
    // Фильтр по проекту
    const matchesProject = !selectedProject || selectedProject === 'all' || camp.project === selectedProject;
    
    // Фильтр по источнику
    const matchesSource = selectedSource === 'all' || camp.source === selectedSource;
    
    // Фильтр по тексту поиска
    const matchesSearch = camp.label.toLowerCase().includes(searchText.toLowerCase());
    
    // Фильтр "только хорошие кампании"
    const matchesGoodFilter = !showOnlyGoodCampaigns || isGoodCampaign(camp.label);
    
    return matchesPlatform && matchesProject && matchesSource && matchesSearch && matchesGoodFilter;
  });

  // Подсчитываем количество выбранных кампаний, которые скрыты фильтром
  const hiddenSelectedCount = selectedCampaigns.filter(campaignValue => {
    const visibleCampaignValues = filteredCampaigns.map(c => c.value);
    return !visibleCampaignValues.includes(campaignValue);
  }).length;

  // Сохраняем в localStorage
  useEffect(() => {
    localStorage.setItem('campaignsSearchText', searchText);
  }, [searchText]);

  useEffect(() => {
    localStorage.setItem('selectedCampaigns', JSON.stringify(selectedCampaigns));
  }, [selectedCampaigns]);

  useEffect(() => {
    localStorage.setItem('selectedMediaSource', selectedSource);
  }, [selectedSource]);

  useEffect(() => {
    localStorage.setItem('showOnlyGoodCampaigns', showOnlyGoodCampaigns.toString());
  }, [showOnlyGoodCampaigns]);

  // Уведомляем родительский компонент
  useEffect(() => {
    if (onFiltersChange) {
      const campaignsToFilter = showOnlyGoodCampaigns 
        ? filteredCampaigns.map(c => c.value) 
        : selectedCampaigns;

      onFiltersChange({
        mediaSource: selectedSource,
        campaigns: campaignsToFilter
      });
    }
  }, [selectedSource, selectedCampaigns, onFiltersChange, showOnlyGoodCampaigns, filteredCampaigns]);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchText('');
  };

  const handleCampaignToggle = (campaignValue) => {
    const isCurrentlySelected = selectedCampaigns.includes(campaignValue);

    if (!isCurrentlySelected && selectedCampaigns.length >= MAX_SELECTED_CAMPAIGNS) {
      return;
    }

    const newSelected = isCurrentlySelected
      ? selectedCampaigns.filter(c => c !== campaignValue)
      : [...selectedCampaigns, campaignValue];

    setSelectAllHint('');
    setSelectedCampaigns(newSelected);
  };

  const handleSelectAll = () => {
    if (filteredCampaigns.length > MAX_SELECTED_CAMPAIGNS) {
      const firstTwenty = capCampaigns(filteredCampaigns.map(c => c.value));
      setSelectedCampaigns(firstTwenty);
      setSelectAllHint(`Выбрано первые ${MAX_SELECTED_CAMPAIGNS} — максимум ${MAX_SELECTED_CAMPAIGNS} кампаний`);
      return;
    }

    const allCampaigns = filteredCampaigns.map(c => c.value);
    setSelectAllHint('');
    setSelectedCampaigns([...new Set([...selectedCampaigns, ...allCampaigns])]);
  };

  const handleClearAll = () => {
    setSelectAllHint('');
    setSelectedCampaigns([]);
  };

  return (
    <div className="flex flex-col h-full space-y-4">
      {/* Выбор источника трафика и платформы в одну строку */}
      <div className="flex-shrink-0">
        <div className="grid grid-cols-2 gap-2">
          {/* Источник трафика */}
          <Select value={selectedSource} onValueChange={setSelectedSource}>
            <SelectTrigger className="w-full h-9 text-xs">
              <SelectValue placeholder="Источник" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все источники</SelectItem>
              {filterOptions.mediaSources.map(source => (
                <SelectItem key={source.value} value={source.value}>
                  <div className="flex items-center gap-2">
                    {getSourceIcon(source.value, 14)}
                    <span>{source.label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Платформа */}
          <Select value={tempPlatform || 'all'} onValueChange={onPlatformChange}>
            <SelectTrigger className="w-full h-9 text-xs">
              <SelectValue placeholder="Платформа" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все платформы</SelectItem>
              <SelectItem value="android">
                <div className="flex items-center gap-2">
                  {getPlatformIcon('android', 14)}
                  <span>Android</span>
                </div>
              </SelectItem>
              <SelectItem value="ios">
                <div className="flex items-center gap-2">
                  {getPlatformIcon('ios', 14)}
                  <span>iOS</span>
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Кампании */}
      <div className="flex-1 min-h-0 flex flex-col">
        <div className="flex-shrink-0 mb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium">Кампании</h3>
              {hiddenSelectedCount > 0 && (
                <Badge variant="outline" className="text-xs">
                  +{hiddenSelectedCount}
                </Badge>
              )}
            </div>
            {/* Чекбокс "Только хорошие кампании" */}
            <div className="flex items-center space-x-1">
              <Checkbox
                id="good-campaigns"
                checked={showOnlyGoodCampaigns}
                onCheckedChange={setShowOnlyGoodCampaigns}
                className="h-3 w-3"
              />
              <label
                htmlFor="good-campaigns"
                className="text-xs leading-none cursor-pointer"
              >
                Хорошие
              </label>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-xs text-muted-foreground py-2">Загрузка...</div>
        ) : (
          <Popover open={isListOpen} onOpenChange={setIsListOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full h-9 justify-between text-xs"
              >
                <span>Выбрано {selectedCampaigns.length} / {MAX_SELECTED_CAMPAIGNS}</span>
                <ChevronDown className="h-3 w-3 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="w-[480px] max-w-[90vw] max-h-[60vh] flex flex-col p-3"
              align="start"
            >
              {/* Поиск и кнопки */}
              <div className="flex-shrink-0 space-y-3 mb-3">
                <div className="space-y-2">
                  <div className="relative">
                    <Input
                      type="text"
                      placeholder="Поиск кампаний..."
                      value={searchText}
                      onChange={handleSearchChange}
                      className="h-8 text-xs pr-8"
                    />
                    {searchText && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                        onClick={handleClearSearch}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  {(searchText || showOnlyGoodCampaigns) && (
                    <div className="text-xs text-muted-foreground">
                      Найдено: {filteredCampaigns.length}
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSelectAll}
                    className="flex-1 h-8 text-xs"
                  >
                    Выбрать все
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleClearAll}
                    className="flex-1 h-8 text-xs"
                  >
                    Снять все
                  </Button>
                </div>

                {selectAllHint && (
                  <div className="text-xs text-amber-600">{selectAllHint}</div>
                )}
                {!selectAllHint && selectedCampaigns.length >= MAX_SELECTED_CAMPAIGNS && (
                  <div className="text-xs text-amber-600">
                    Максимум {MAX_SELECTED_CAMPAIGNS} кампаний
                  </div>
                )}
              </div>

              {/* Список кампаний */}
              <div className="flex-1 min-h-0 border rounded-md">
                <ScrollArea className="h-full max-h-[45vh]">
                  <div className="p-2 space-y-1">
                    {filteredCampaigns.length === 0 && searchText ? (
                      <div className="text-xs text-muted-foreground py-2 text-center">
                        Ничего не найдено
                      </div>
                    ) : filteredCampaigns.length === 0 ? (
                      <div className="text-xs text-muted-foreground py-2 text-center">
                        Нет доступных кампаний
                      </div>
                    ) : (
                      filteredCampaigns.map(camp => {
                        const isSelected = selectedCampaigns.includes(camp.value);
                        const isDisabled = !isSelected && selectedCampaigns.length >= MAX_SELECTED_CAMPAIGNS;

                        return (
                          <div
                            key={camp.value}
                            className={`flex items-center space-x-2 p-2 rounded-md hover:bg-accent ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                            onClick={() => !isDisabled && handleCampaignToggle(camp.value)}
                          >
                            <Checkbox
                              checked={isSelected}
                              disabled={isDisabled}
                              onCheckedChange={() => handleCampaignToggle(camp.value)}
                              className="h-4 w-4"
                            />
                            <span className="text-sm flex-1 truncate" title={camp.label}>
                              {camp.label}
                            </span>
                            <div className="flex items-center gap-2 shrink-0">
                              {getSourceIcon(camp.source, 16) ? (
                                <div className="flex items-center">
                                  {getSourceIcon(camp.source, 16)}
                                </div>
                              ) : (
                                <Badge variant="outline" className="text-xs">
                                  {getShortSourceName(camp.source)}
                                </Badge>
                              )}
                              {camp.pf && getPlatformIcon(camp.pf, 16)}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </ScrollArea>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );
};

export default CampaignsFilter;
